import { supabase } from '@/integrations/supabase/client';

/**
 * Secure Mapbox API wrapper that uses edge function proxy
 * This prevents API token exposure in the frontend
 */
export class SecureMapboxClient {
  private static instance: SecureMapboxClient;

  private constructor() {}

  static getInstance(): SecureMapboxClient {
    if (!SecureMapboxClient.instance) {
      SecureMapboxClient.instance = new SecureMapboxClient();
    }
    return SecureMapboxClient.instance;
  }

  /**
   * Make a request to Mapbox API through our secure proxy
   */
  async request(endpoint: string): Promise<any> {
    try {
      const { data, error } = await supabase.functions.invoke('mapbox-proxy', {
        body: { endpoint }
      });

      if (error) {
        console.error('Mapbox proxy error:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch from Mapbox:', error);
      throw error;
    }
  }

  /**
   * Geocode an address to coordinates
   */
  async geocode(address: string): Promise<[number, number] | null> {
    try {
      const endpoint = `/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`;
      const data = await this.request(endpoint);
      
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        return [lat, lng];
      }
      
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  }

  /**
   * Reverse geocode coordinates to address
   */
  async reverseGeocode(lat: number, lng: number): Promise<string | null> {
    try {
      const endpoint = `/geocoding/v5/mapbox.places/${lng},${lat}.json`;
      const data = await this.request(endpoint);
      
      if (data.features && data.features.length > 0) {
        return data.features[0].place_name;
      }
      
      return null;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  }
}

// Export singleton instance
export const mapboxClient = SecureMapboxClient.getInstance();

/**
 * Clean up any existing Mapbox tokens from localStorage
 * This should be called on app initialization
 */
export function cleanupStoredTokens() {
  // Remove any stored Mapbox tokens from localStorage
  const keysToRemove = [
    'mapbox_access_token',
    'mapbox_token',
    'MAPBOX_TOKEN',
    'mapboxToken'
  ];
  
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Also check sessionStorage
  keysToRemove.forEach(key => {
    sessionStorage.removeItem(key);
  });
  
  console.log('Cleaned up stored API tokens for security');
}